import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  EyeOff,
  Clock,
  Search,
  RefreshCw,
  Star,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Globe,
  Building2,
  Tag,
} from "lucide-react";
import API from "../lib/api";
import AdminGuard from "../components/admin/AdminGuard";
import AdminShell from "../components/admin/AdminShell";
import { errorMessage } from "../lib/errors";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

/**
 * Phase 7.5 — Admin Business Listings (Marketplace approval queue)
 * ----------------------------------------------------------------
 * Tabs: Pending / Active / Hidden / Expired / Rejected
 * Per-listing actions: View, Approve, Reject (with reason), Hide, Expire,
 *                      Toggle Founding Partner badge.
 * Search across name / category / city / country / contact_email.
 *
 * URL is stateful (?status=pending) so the admin can deep-link directly into a tab.
 */
const STATUS_TABS = [
  { key: "pending", icon: Clock, gold: true },
  { key: "active", icon: CheckCircle2 },
  { key: "hidden", icon: EyeOff },
  { key: "expired", icon: Clock },
  { key: "rejected", icon: XCircle },
];

export default function AdminBusinessesPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = (searchParams.get("status") || "pending").toLowerCase();
  const [status, setStatus] = useState(STATUS_TABS.some((tab) => tab.key === initialStatus) ? initialStatus : "pending");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [actingId, setActingId] = useState(null);
  const [selected, setSelected] = useState(null); // detail drawer
  const [rejectFor, setRejectFor] = useState(null); // {business_id, name}
  const [hideFor, setHideFor] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = () => setRefreshTick((n) => n + 1);

  // Sync status + q to URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("status", status);
    if (q.trim()) next.set("q", q.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);

  // Load stats + items whenever tab/search/refresh changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [listRes, statsRes] = await Promise.all([
          API.get("/admin/businesses", { params: { status, q: q.trim() || undefined, limit: 100 } }),
          API.get("/admin/businesses/stats"),
        ]);
        if (cancelled) return;
        setItems(listRes.data.items || []);
        setStats(statsRes.data || null);
      } catch (err) {
        if (!cancelled) toast.error(errorMessage(err, t("admin.biz.load_failed", "Couldn\u2019t load businesses")));
      } finally {
        if (!cancelled) setLoading(false);
        setRefreshing(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q, refreshTick]);

  const runAction = async (id, action, body = null, successMsg = "") => {
    setActingId(id);
    try {
      const url = `/admin/businesses/${id}/${action}`;
      const { data } = body ? await API.post(url, body) : await API.post(url);
      if (data && data.item) {
        // Optimistic update: remove from current tab if status changed away
        setItems((prev) => prev.filter((b) => b.business_id !== id || data.item.status === status));
      }
      toast.success(successMsg || t("admin.biz.action_done", "Done"));
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, t("admin.biz.action_failed", "Action failed")));
    } finally {
      setActingId(null);
    }
  };

  const handleApprove = (b) => {
    if (!window.confirm(t("admin.biz.confirm_approve", "Approve and publish \u201C{{name}}\u201D to the marketplace?", { name: b.name }))) return;
    runAction(b.business_id, "approve", null, t("admin.biz.approved_toast", "{{name}} is now live", { name: b.name }));
  };
  const handleExpire = (b) => {
    if (!window.confirm(t("admin.biz.confirm_expire", "Expire \u201C{{name}}\u201D?", { name: b.name }))) return;
    runAction(b.business_id, "expire", null, t("admin.biz.expired_toast", "{{name}} expired", { name: b.name }));
  };
  const handleReactivate = (b) => {
    runAction(b.business_id, "reactivate", null, t("admin.biz.reactivated_toast", "{{name}} reactivated", { name: b.name }));
  };
  const handleToggleFounding = (b) => {
    runAction(b.business_id, "founding-partner", { is_founding_partner: !b.is_founding_partner },
      b.is_founding_partner
        ? t("admin.biz.fp_removed_toast", "Removed Founding Partner badge from {{name}}", { name: b.name })
        : t("admin.biz.fp_added_toast", "{{name}} marked as Founding Partner", { name: b.name }));
  };

  const handleConfirmReject = async () => {
    if (!rejectFor) return;
    const reason = (actionReason || "").trim();
    if (!reason) {
      toast.error(t("admin.biz.reject_reason_required", "Please give a reason so the owner can act on it."));
      return;
    }
    await runAction(rejectFor.business_id, "reject", { reason },
      t("admin.biz.rejected_toast", "{{name}} rejected", { name: rejectFor.name }));
    setRejectFor(null);
    setActionReason("");
  };
  const handleConfirmHide = async () => {
    if (!hideFor) return;
    await runAction(hideFor.business_id, "hide", { reason: (actionReason || "").trim() || undefined },
      t("admin.biz.hidden_toast", "{{name}} hidden", { name: hideFor.name }));
    setHideFor(null);
    setActionReason("");
  };

  const tabBadge = (key) => {
    if (!stats) return null;
    const n = stats[key] ?? 0;
    if (!n) return null;
    return <span className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/10 text-white/85" data-testid={`biz-tab-count-${key}`}>{n}</span>;
  };

  const ActionRow = ({ b }) => (
    <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`biz-actions-${b.business_id}`}>
      <button
        onClick={() => setSelected(b)}
        className="btn-ghost text-xs inline-flex items-center gap-1"
        data-testid={`biz-view-${b.business_id}`}
      >
        <ExternalLink className="w-3 h-3" /> {t("admin.biz.view", "View")}
      </button>
      {b.status === "pending" && (
        <>
          <button
            onClick={() => handleApprove(b)}
            disabled={actingId === b.business_id}
            className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`biz-approve-${b.business_id}`}
          >
            {actingId === b.business_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            {t("admin.biz.approve", "Approve")}
          </button>
          <button
            onClick={() => { setRejectFor({ business_id: b.business_id, name: b.name }); setActionReason(""); }}
            disabled={actingId === b.business_id}
            className="btn-ghost text-xs inline-flex items-center gap-1 text-rose-300 hover:text-rose-200 disabled:opacity-50"
            data-testid={`biz-reject-${b.business_id}`}
          >
            <XCircle className="w-3 h-3" /> {t("admin.biz.reject", "Reject")}
          </button>
        </>
      )}
      {b.status === "active" && (
        <>
          <button
            onClick={() => { setHideFor({ business_id: b.business_id, name: b.name }); setActionReason(""); }}
            disabled={actingId === b.business_id}
            className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`biz-hide-${b.business_id}`}
          >
            <EyeOff className="w-3 h-3" /> {t("admin.biz.hide", "Hide")}
          </button>
          <button
            onClick={() => handleExpire(b)}
            disabled={actingId === b.business_id}
            className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`biz-expire-${b.business_id}`}
          >
            <Clock className="w-3 h-3" /> {t("admin.biz.expire", "Expire")}
          </button>
          <button
            onClick={() => handleToggleFounding(b)}
            disabled={actingId === b.business_id}
            className={`btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50 ${b.is_founding_partner ? "text-amber-300" : ""}`}
            data-testid={`biz-toggle-fp-${b.business_id}`}
          >
            <Star className={`w-3 h-3 ${b.is_founding_partner ? "fill-current" : ""}`} />
            {b.is_founding_partner
              ? t("admin.biz.fp_remove", "Unmark Founding")
              : t("admin.biz.fp_add", "Mark as Founding")}
          </button>
        </>
      )}
      {(b.status === "hidden" || b.status === "expired" || b.status === "rejected") && (
        <button
          onClick={() => handleReactivate(b)}
          disabled={actingId === b.business_id}
          className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
          data-testid={`biz-reactivate-${b.business_id}`}
        >
          {actingId === b.business_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          {t("admin.biz.reactivate", "Reactivate")}
        </button>
      )}
    </div>
  );

  return (
    <AdminGuard>
      <AdminShell active="businesses">
        <div className="space-y-6" data-testid="admin-businesses">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow mb-2">{t("admin.biz.eyebrow", "\u2014 Marketplace")}</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="admin-biz-title">
                {t("admin.biz.title", "Business Listings")}
              </h2>
              <p className="text-sm text-white/65 mt-2 max-w-2xl leading-relaxed">
                {t("admin.biz.subtitle", "Review submissions, approve listings to publish them on the public marketplace, and manage Founding Partner badges.")}
              </p>
            </div>
            <button
              onClick={() => { setRefreshing(true); refresh(); }}
              disabled={refreshing || loading}
              className="btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="biz-refresh-btn"
            >
              {(refreshing || loading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t("admin.biz.refresh", "Refresh")}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-1.5 border-b border-white/10 pb-1">
            {STATUS_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = status === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setStatus(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-t-md border-b-2 transition-colors ${
                    isActive
                      ? "border-[#C8A24A] text-white"
                      : "border-transparent text-white/55 hover:text-white/90"
                  }`}
                  data-testid={`biz-tab-${tab.key}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${tab.gold && isActive ? "gold-text" : ""}`} />
                  {t(`admin.biz.tab_${tab.key}`, tab.key)}
                  {tabBadge(tab.key)}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("admin.biz.search_placeholder", "Search by name, category, city, country, email\u2026")}
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#0F1116] border border-white/10 rounded-lg focus:outline-none focus:border-[#C8A24A]/40"
                data-testid="biz-search-input"
              />
            </div>
            <span className="text-xs text-white/45 font-mono" data-testid="biz-count">
              {loading ? "\u2026" : `${items.length} ${items.length === 1 ? t("admin.biz.result", "result") : t("admin.biz.results", "results")}`}
            </span>
          </div>

          {/* List */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={`biz-skel-${idx}`} className="card-surface p-4 animate-pulse">
                  <div className="h-4 w-2/3 bg-white/10 rounded mb-2" />
                  <div className="h-3 w-1/3 bg-white/10 rounded mb-3" />
                  <div className="h-12 bg-white/5 rounded" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="card-surface p-10 text-center" data-testid="biz-empty">
              <Building2 className="w-8 h-8 mx-auto opacity-50 mb-3" />
              <h3 className="font-display text-lg">
                {t("admin.biz.empty_title", "No listings in this tab")}
              </h3>
              <p className="text-sm text-white/55 mt-1">
                {status === "pending"
                  ? t("admin.biz.empty_pending_sub", "When a business submits a listing, it will appear here for review.")
                  : t("admin.biz.empty_other_sub", "Submit a listing from the marketplace, or change tabs to see existing ones.")}
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((b) => (
                <BusinessCard
                  key={b.business_id}
                  b={b}
                  ActionRow={ActionRow}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>

        {/* Detail drawer */}
        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="bg-[#0F1116] border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <Building2 className="w-4 h-4 gold-text" />
                {selected?.name}
                {selected?.is_founding_partner && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 inline-flex items-center gap-1">
                    <Star className="w-2.5 h-2.5 fill-current" /> {t("admin.biz.fp_badge", "Founding Partner")}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription className="text-white/55 text-xs">
                {selected?.slug && (<span className="font-mono">/{selected.slug}</span>)} · {selected?.status}
              </DialogDescription>
            </DialogHeader>
            {selected && <BusinessDetailBody b={selected} t={t} />}
            <DialogFooter className="border-t border-white/10 pt-3">
              {selected && <ActionRow b={selected} />}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject reason dialog */}
        <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
          <DialogContent className="bg-[#0F1116] border-white/10 text-white max-w-md" data-testid="biz-reject-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg text-rose-300">
                {t("admin.biz.reject_title", "Reject listing")}
              </DialogTitle>
              <DialogDescription className="text-white/65 text-sm">
                {t("admin.biz.reject_help", "The owner will receive an email with your reason. Be specific so they can fix it and resubmit.")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/55 block">
                {t("admin.biz.reject_reason_label", "Reason (sent to owner)")}
              </label>
              <textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                rows={4}
                placeholder={t("admin.biz.reject_reason_placeholder", "e.g., Description is too short \u2014 please add details about your services and qualifications.")}
                className="w-full bg-[#15171D] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400/40"
                data-testid="biz-reject-reason"
              />
            </div>
            <DialogFooter>
              <button onClick={() => setRejectFor(null)} className="btn-ghost text-sm">
                {t("common.cancel", "Cancel")}
              </button>
              <button
                onClick={handleConfirmReject}
                disabled={actingId === rejectFor?.business_id}
                className="btn-primary text-sm bg-gradient-to-r from-rose-500 to-rose-700 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="biz-reject-confirm"
              >
                {actingId === rejectFor?.business_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                {t("admin.biz.reject_confirm_btn", "Reject & notify owner")}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Hide reason dialog */}
        <Dialog open={!!hideFor} onOpenChange={(o) => !o && setHideFor(null)}>
          <DialogContent className="bg-[#0F1116] border-white/10 text-white max-w-md" data-testid="biz-hide-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg">
                {t("admin.biz.hide_title", "Hide listing")}
              </DialogTitle>
              <DialogDescription className="text-white/65 text-sm">
                {t("admin.biz.hide_help", "The listing will be removed from the public marketplace but kept on file. You can reactivate it at any time. The owner is not notified.")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/55 block">
                {t("admin.biz.hide_reason_label", "Internal note (optional)")}
              </label>
              <textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                rows={3}
                className="w-full bg-[#15171D] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40"
                data-testid="biz-hide-reason"
              />
            </div>
            <DialogFooter>
              <button onClick={() => setHideFor(null)} className="btn-ghost text-sm">
                {t("common.cancel", "Cancel")}
              </button>
              <button
                onClick={handleConfirmHide}
                disabled={actingId === hideFor?.business_id}
                className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="biz-hide-confirm"
              >
                {actingId === hideFor?.business_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3" />}
                {t("admin.biz.hide_confirm_btn", "Hide listing")}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AdminShell>
    </AdminGuard>
  );
}

const BusinessCard = ({ b, ActionRow, t }) => {
  const initials = useMemo(() => (b.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase(), [b.name]);
  return (
    <div className="card-surface p-4 flex flex-col h-full" data-testid={`biz-card-${b.business_id}`}>
      <div className="flex items-start gap-3">
        {/* Avatar / logo placeholder */}
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#C8A24A]/30 to-[#7A5F2A]/30 flex items-center justify-center gold-text font-display text-base shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-base truncate" title={b.name}>{b.name}</h3>
            {b.is_founding_partner && (
              <Star className="w-3.5 h-3.5 text-amber-300 fill-current shrink-0" />
            )}
          </div>
          <div className="text-[11px] text-white/55 truncate" title={b.category}>{b.category}</div>
          {(b.city || b.country) && (
            <div className="flex items-center gap-1 text-[11px] text-white/50 mt-0.5">
              <MapPin className="w-3 h-3" />
              {[b.city, b.country].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
      </div>
      {b.tagline && (
        <p className="text-xs text-white/65 mt-2 line-clamp-2 leading-relaxed" title={b.tagline}>{b.tagline}</p>
      )}
      <div className="mt-2 space-y-1 text-[11px] text-white/55">
        {b.contact_email && (
          <div className="flex items-center gap-1.5 truncate">
            <Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{b.contact_email}</span>
          </div>
        )}
        {b.owner_email && b.owner_email !== b.contact_email && (
          <div className="flex items-center gap-1.5 truncate" title={`Owner: ${b.owner_email}`}>
            <Building2 className="w-3 h-3 shrink-0" /> <span className="truncate">{t("admin.biz.owner_prefix", "Owner")}: {b.owner_email}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 truncate">
          <Clock className="w-3 h-3 shrink-0" />
          <span className="truncate">{new Date(b.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="mt-auto">
        <ActionRow b={b} />
      </div>
    </div>
  );
};

const BusinessDetailBody = ({ b, t }) => (
  <div className="space-y-4 text-sm" data-testid="biz-detail-body">
    {b.tagline && (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1">{t("admin.biz.detail_tagline", "Tagline")}</div>
        <div className="italic text-white/85">{b.tagline}</div>
      </div>
    )}
    {b.description && (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1">{t("admin.biz.detail_description", "Description")}</div>
        <div className="text-white/85 whitespace-pre-wrap leading-relaxed max-h-[260px] overflow-auto">{b.description}</div>
      </div>
    )}
    <div className="grid grid-cols-2 gap-3">
      <DetailField icon={Tag} label={t("admin.biz.detail_category", "Category")} value={b.category} />
      <DetailField icon={MapPin} label={t("admin.biz.detail_location", "Location")} value={[b.city, b.country].filter(Boolean).join(", ")} />
      <DetailField icon={Mail} label={t("admin.biz.detail_contact_email", "Contact email")} value={b.contact_email} mono />
      <DetailField icon={Phone} label={t("admin.biz.detail_contact_phone", "Contact phone")} value={b.contact_phone} mono />
      {b.website && (
        <DetailField icon={Globe} label={t("admin.biz.detail_website", "Website")} value={
          <a href={b.website} target="_blank" rel="noreferrer" className="gold-text underline truncate">{b.website}</a>
        } />
      )}
      {b.owner_email && (
        <DetailField icon={Building2} label={t("admin.biz.detail_owner_email", "Owner email")} value={b.owner_email} mono />
      )}
    </div>
    {(b.services || []).length > 0 && (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1.5">{t("admin.biz.detail_services", "Services")}</div>
        <div className="flex flex-wrap gap-1.5">
          {b.services.map((s) => (
            <span key={`svc-${s}`} className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 bg-white/5">{s}</span>
          ))}
        </div>
      </div>
    )}
    {(b.dosha_alignment || []).length > 0 && (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1.5">{t("admin.biz.detail_doshas", "Dosha alignment")}</div>
        <div className="flex flex-wrap gap-1.5">
          {b.dosha_alignment.map((d) => (
            <span key={`d-${d}`} className="text-[11px] px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-200">{d}</span>
          ))}
        </div>
      </div>
    )}
    {b.status === "rejected" && b.status_reason && (
      <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
        <div className="text-[10px] uppercase tracking-wider text-rose-300 mb-1">{t("admin.biz.detail_reject_reason", "Rejection reason sent to owner")}</div>
        <div className="text-sm text-white/80 whitespace-pre-wrap">{b.status_reason}</div>
      </div>
    )}
  </div>
);

const DetailField = ({ icon: Icon, label, value, mono }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/55 mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-sm text-white/85 truncate ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
};
